import type { EchoQcFinding, EchoflicksVideoSession } from "../types";

// ---------------------------------------------------------------------------
// Pure helpers that turn the projected QC verdict (session.qc_* fields, from
// the composer's aggregate QCRunReport) into a PM-readable summary.
//
// Key framing: QC is OBSERVE-ONLY. A "failed" aggregate status means QC flagged
// issues but did NOT block delivery — never present it as "the video is broken".
// ---------------------------------------------------------------------------

export type QcVerdict =
  | "passed"
  | "flagged"
  | "repaired"
  | "skipped"
  | "unknown";

export type QcLayer = { layer_name: string; status: string };

export type QcSummary = {
  ran: boolean;
  verdict: QcVerdict;
  aggregateStatus: string;
  /** Did QC actually block delivery? Observe-only ⇒ false (only failed_closed blocks). */
  blocked: boolean;
  critical: number;
  warning: number;
  info: number;
  totalIssues: number;
  layers: QcLayer[];
  findings: EchoQcFinding[]; // sorted critical → warning → info
};

const SEVERITY_RANK: Record<string, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

export function severityRank(severity?: string | null): number {
  return SEVERITY_RANK[(severity ?? "").toLowerCase()] ?? 3;
}

const AGENT_LABELS: Record<string, string> = {
  audio_visual: "Audio ↔ visual timing",
  "audio_visual:semantic": "Narration ↔ visual match",
  semantic: "Narration ↔ visual match",
  i2v_generation: "Clip generation (technical)",
  i2v_semantic: "Framing & subjects",
  expression: "Faces & expressions",
  voiceover_generation: "Voiceover audio",
  music_generation: "Music",
  music_mix_output: "Music mix",
  timeline: "Timeline & duration",
  duration: "Timeline & duration",
};

/** Friendly, PM-readable name for a QC layer; falls back to the raw layer name. */
export function qcAgentLabel(layerName?: string | null): string {
  if (!layerName) return "Quality check";
  return AGENT_LABELS[layerName] ?? layerName.replace(/[_:]+/g, " ");
}

export function qcVerdictLabel(verdict: QcVerdict): string {
  switch (verdict) {
    case "passed":
      return "Passed";
    case "repaired":
      return "Auto-corrected";
    case "flagged":
      return "Flagged";
    case "skipped":
      return "Not run";
    default:
      return "Unknown";
  }
}

function toVerdict(status: string): QcVerdict {
  switch (status) {
    case "passed":
      return "passed";
    case "repaired":
      return "repaired";
    case "warning":
    case "failed":
    case "error":
      return "flagged";
    case "skipped":
      return "skipped";
    default:
      return "unknown";
  }
}

export function summarizeQc(
  session: EchoflicksVideoSession | null | undefined,
): QcSummary | null {
  if (!session) return null;
  const status = session.qc_aggregate_status ?? null;
  const counts = session.qc_issue_counts ?? null;
  const layerStatus = session.qc_layer_status ?? null;
  const findings = session.qc_top_findings ?? null;

  // No QC signal projected at all → render nothing.
  if (!status && !counts && !layerStatus && !findings) return null;

  const aggregateStatus = status ?? "unknown";
  const critical = counts?.critical ?? 0;
  const warning = counts?.warning ?? 0;
  const info = counts?.info ?? 0;
  const layers: QcLayer[] = layerStatus
    ? Object.entries(layerStatus).map(([layer_name, s]) => ({
        layer_name,
        status: String(s),
      }))
    : [];
  const sortedFindings = (findings ?? [])
    .slice()
    .sort((a, b) => severityRank(a.severity) - severityRank(b.severity));

  return {
    ran: aggregateStatus !== "skipped" && aggregateStatus !== "unknown",
    verdict: toVerdict(aggregateStatus),
    aggregateStatus,
    blocked: session.qc_mode === "failed_closed",
    critical,
    warning,
    info,
    totalIssues: critical + warning + info,
    layers,
    findings: sortedFindings,
  };
}

// ---------------------------------------------------------------------------
// Image orientation (Track B) — derive portrait/landscape/square from pixel
// dimensions when the backend hasn't projected an `orientation` yet.
// ---------------------------------------------------------------------------

export type Orientation = "portrait" | "landscape" | "square";

export function orientationFromDimensions(
  width?: number | null,
  height?: number | null,
): Orientation | null {
  if (!width || !height || width <= 0 || height <= 0) return null;
  if (width === height) return "square";
  return height > width ? "portrait" : "landscape";
}
